import { testRegex, REGEX } from "@/backend/lib/regex";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_SUBJECT_TYPES, NUMBER_CUSTOM_FIELD_TYPES, CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ParameterInputType } from "@/types/parameterTypes";
import { Parameter } from "@/backend/models/parameter";
import { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { BasicButton } from "../buttons/BasicButton";
import { GenerateObjectValues } from "../customFIelds/customFieldObjects/GenerateObjectValues";
import { Plus } from "lucide-react";
import { ButtonParent } from "../buttons/ButtonParent";

export function AddValueToParameterButtons(props: { parameter: Parameter }) {
  const { parameter } = props;
  const createCustomField = useParameterStore(state => state.createCustomField);
  const { objectShapeId } = parameter;

  const onAddValue = () => {
    void createCustomField({
      subjectId: parameter.id,
      subjectType: CUSTOM_FIELD_SUBJECT_TYPES.PARAMETER,
      fieldType: parameter.fieldType,
      isSelected: false,
      objectInstance:  {
        objectShapeId,
        title: `${I18n.get('object')} ${parameter.fields.length + 1}`,
      },
    });
  };

  return (
    <div className='d-flex gap-1'>
      { parameter.inputType !== ParameterInputType.FREE_FORM && parameter.inputType !== ParameterInputType.TABLE && <ButtonParent
          className='fusa-btn-small-with-icon fusa-btn-primary'
          title={I18n.get('addValue')}
          onClick={onAddValue}
        >
          <Plus size={16} />
          <span>{parameter.fieldType === CUSTOM_FIELD_TYPES.Object ? I18n.get('addObject') : I18n.get('addListItem')}</span>
        </ButtonParent>
      }
      {NUMBER_CUSTOM_FIELD_TYPES.includes(parameter.fieldType) && (
        <GenerateValues parameter={parameter} />
      )}
      {parameter.fieldType === CUSTOM_FIELD_TYPES.Object && parameter.propertyKeys.every(key => NUMBER_CUSTOM_FIELD_TYPES.includes(key.type)) && (
        <GenerateObjectValues parameter={parameter} />
      )}
    </div>
  );
}

function GenerateValues(props: { parameter: Parameter }) {
  const { parameter } = props;
  const [startingValue, setStartingValue] = useState('');
  const [endingValue, setEndingValue] = useState('');
  const [stepValue, setStepValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEditValues, setShowEditValues] = useState(false);
  const generateParameterValues = useParameterStore(state => state.generateParameterValues);
  const [startingValueError, setStartingValueError] = useState('');
  const [endingValueError, setEndingValueError] = useState('');
  const [stepValueError, setStepValueError] = useState('');

  const onConfirm = async () => {
    const startingValueIsValid = validateInput(startingValue, setStartingValue, setStartingValueError);
    const endingValueIsValid = validateInput(endingValue, setEndingValue, setEndingValueError);
    const stepValueIsValid = validateInput(stepValue, setStepValue, setStepValueError);
    if (!startingValueIsValid || !endingValueIsValid || !stepValueIsValid) {
      return;
    }
    setIsGenerating(true);
    await generateParameterValues({
      parameterId: parameter.id,
      startingValue,
      endingValue,
      stepValue,
    });
    setIsGenerating(false);
    setShowEditValues(false);
  };

  const validateInput = (newValue: string, setValue: (value: string) => void, setValueError: (value: string) => void): boolean => {
    if (newValue === '') {
      setValueError(I18n.get('pleaseEnterAValue'));
      return false;
    }
    if (parameter.fieldType === CUSTOM_FIELD_TYPES.Float) {
      if (!testRegex(REGEX.float, newValue)) {
        setValueError(I18n.get('valueDoesNotMatchType', { value: newValue, type: I18n.get(parameter.fieldType) }));
        return false;
      }
    }
    if (parameter.fieldType === CUSTOM_FIELD_TYPES.Integer) {
      if (!testRegex(REGEX.integer, newValue)) {
        setValueError(I18n.get('valueDoesNotMatchType', { value: newValue, type: I18n.get(parameter.fieldType) }));
        return false;
      }
    }
    setValue(newValue);
    setValueError('');
    return true;
  };

  const hasErrors = startingValueError !== '' || endingValueError !== '' || stepValueError !== '';

  return (
    <>
      <ButtonParent
        className='fusa-btn-small-with-icon fusa-btn-primary'
        title={I18n.get('plusRangedValues')}
        onClick={() => setShowEditValues(true)}
      >
        <Plus size={16} />
        <span>{I18n.get('addRangedValues')}</span>
      </ButtonParent>
      <Modal size='sm' show={showEditValues} onHide={() => setShowEditValues(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('addRangedValues')}</Modal.Title>
        </Modal.Header >
        <Modal.Body>
          <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('startingValue')}</Form.Label>
          <Form.Control
            size='sm'
            type='number'
            aria-label={I18n.get('enterField', { field: I18n.get('startingValue') })}
            value={startingValue}
            onBlur={() => validateInput(startingValue, setStartingValue, setStartingValueError)}
            onChange={(event) => setStartingValue(event.target.value)}
            isInvalid={!!startingValueError}
          />
          <Form.Control.Feedback type="invalid">
            { startingValueError }
          </Form.Control.Feedback>
          <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('endingValue')}</Form.Label>
          <Form.Control
            size='sm'
            type='number'
            aria-label={I18n.get('enterField', { field: I18n.get('endingValue') })}
            value={endingValue}
            onBlur={() => validateInput(endingValue, setEndingValue, setEndingValueError)}
            onChange={(event) => setEndingValue(event.target.value)}
            isInvalid={!!endingValueError}
          />
          <Form.Control.Feedback type="invalid">
            { endingValueError }
          </Form.Control.Feedback>
          <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('increment')}</Form.Label>
          <Form.Control
            size='sm'
            type='number'
            aria-label={I18n.get('enterField', { field: I18n.get('increment') })}
            value={stepValue}
            onBlur={() => validateInput(stepValue, setStepValue, setStepValueError)}
            onChange={(event) => setStepValue(event.target.value)}
            className='mb-2'
            isInvalid={!!stepValueError}
          />
          <Form.Control.Feedback type="invalid">
            { stepValueError }
          </Form.Control.Feedback>
          <BasicButton
            className='btn btn-sm btn-primary'
            label={I18n.get('confirm')}
            onClick={onConfirm}
            processing={isGenerating}
            disabled={hasErrors}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
